import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { FavoriteLocation, FavoritePreference, FavoriteStop, FavoriteTicket } from './favorites.model';
import { JsonUtil } from '@ridango/core/lib/utils/json-util';
import Env from '@ridango/core/env';
import { AuthService } from '@ridango/core';
import { map, tap } from 'rxjs/operators';
import { FavoriteSchedule } from '../../lib/favorites/favorites.model';
import { OpenTripPlannerService } from '../../lib/open-trip-planner';

@Injectable({
    providedIn: 'root'
})
export class FavoritesService {
    readonly favoriteStopsLocalStorageKey = this.env.APP_NAME + '-favorites-favorite-stop';
    readonly favoriteScheduleLocalStorageKey = this.env.APP_NAME + '-favorites-favorite-schedule';
    readonly favoriteLocationsLocalStorageKey = this.env.APP_NAME + '-favorites-favorite-locations';
    readonly favoriteTicketsLocalStorageKey = this.env.APP_NAME + '-favorites-favorite-tickets';
    readonly favoritePreferenceLocalStorageKey = this.env.APP_NAME + '-favorites-favorite-preference';
    readonly channel = 'mobile';

    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private authService: AuthService,
        private openTripPlannerService: OpenTripPlannerService
    ) {}

    getFavoriteStopsFromLocalStorage(): FavoriteStop[] {
        return JsonUtil.parse<FavoriteStop[]>(
            localStorage.getItem(this.favoriteStopsLocalStorageKey),
            Array.isArray,
            []
        );
    }

    getFavoriteStops(): Observable<FavoriteStop[]> {
        if (this.authService.getLoginStatus()) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/stop`;

            return this.http.get<FavoriteStop[]>(url);
        } else {
            return of(this.getFavoriteStopsFromLocalStorage());
        }
    }

    addFavoriteStop(payload): Observable<FavoriteStop> {
        if (this.authService.getLoginStatus()) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/stop`;

            return this.http.post<FavoriteStop>(url, payload);
        } else {
            try {
                const prevValue = this.getFavoriteStopsFromLocalStorage();

                localStorage.setItem(this.favoriteStopsLocalStorageKey, JSON.stringify([...prevValue, payload]));

                return of(payload);
            } catch (err) {
                console.error('Favorites.addFavorites', err);
            }
        }
    }

    updateFavoriteStop(payload): Observable<FavoriteStop> {
        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/stops/favorite`;

        return this.http.put<FavoriteStop>(url, payload);
    }

    deleteFavoriteStop(stopId): Observable<string> {
        if (this.authService.getLoginStatus()) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/stop/${stopId}`;

            return this.http.delete<string>(url);
        } else {
            try {
                const prevValue = this.getFavoriteStopsFromLocalStorage();

                localStorage.setItem(
                    this.favoriteStopsLocalStorageKey,
                    JSON.stringify(prevValue.filter((s) => s.stopId !== stopId))
                );

                return of(stopId);
            } catch (err) {
                console.error('Favorites.deleteFromFavorite', err);
            }
        }
    }

    saveFavoriteStopsFromLocalStorage(currentFavoriteStopIds: string[]): Observable<FavoriteStop[]> {
        if (!this.authService.getLoginStatus()) {
            return of([]);
        }

        const stops = this.getFavoriteStopsFromLocalStorage();

        const uniqueStops = stops.filter(
            (stop) => !currentFavoriteStopIds.some((currentStopId) => currentStopId === stop.stopId)
        );

        localStorage.setItem(this.favoriteStopsLocalStorageKey, JSON.stringify(uniqueStops));

        if (!uniqueStops) {
            return of([]);
        }

        return forkJoin(
            uniqueStops.map((stop: FavoriteStop) =>
                this.addFavoriteStop(stop).pipe(
                    tap((stop) => {
                        const stops = this.getFavoriteStopsFromLocalStorage();

                        localStorage.setItem(
                            this.favoriteStopsLocalStorageKey,
                            JSON.stringify(stops.filter((lStop) => stop.stopId !== lStop.stopId))
                        );
                    })
                )
            )
        );
    }

    getFavoriteLocationsFromLocalStorage(): FavoriteLocation[] {
        return JsonUtil.parse<FavoriteLocation[]>(
            localStorage.getItem(this.favoriteLocationsLocalStorageKey),
            Array.isArray,
            []
        );
    }

    getFavoriteLocations(): Observable<FavoriteLocation[]> {
        if (this.authService.getLoginStatus()) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/location/channel/${this.channel}`;

            return this.http.get<FavoriteLocation[]>(url);
        } else {
            return of(this.getFavoriteLocationsFromLocalStorage());
        }
    }

    addFavoriteLocation(payload): Observable<FavoriteLocation> {
        if (this.authService.getLoginStatus()) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/location/channel/${this.channel}`;

            return this.http.post<FavoriteLocation>(url, payload);
        } else {
            try {
                const prevValue = this.getFavoriteLocationsFromLocalStorage();

                localStorage.setItem(
                    this.favoriteLocationsLocalStorageKey,
                    JSON.stringify([
                        ...prevValue,
                        {
                            name: payload.name,
                            latitude: payload.latitude,
                            longitude: payload.longitude,
                            pointOfInterest: payload.pointOfInterest
                        }
                    ])
                );

                return of({
                    name: payload.name,
                    latitude: payload.latitude,
                    longitude: payload.longitude,
                    pointOfInterest: payload.pointOfInterest
                });
            } catch (err) {
                console.error('Favorites.addFavorites', err);
            }
        }
    }

    updateFavoriteLocation(location): Observable<FavoriteLocation> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/location/channel/${this.channel}/${location.id}`;

        return this.http.put<FavoriteLocation>(url, location);
    }

    deleteFavoriteLocation(location: FavoriteLocation): Observable<FavoriteLocation> {
        if (this.authService.getLoginStatus()) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/location/channel/${this.channel}/${location.id}`;

            return this.http.delete<FavoriteLocation>(url);
        } else {
            try {
                const prevValue = this.getFavoriteLocationsFromLocalStorage();

                localStorage.setItem(
                    this.favoriteLocationsLocalStorageKey,
                    JSON.stringify(
                        prevValue.filter(
                            (l) => !(location.latitude === l.latitude && location.longitude === l.longitude)
                        )
                    )
                );

                return of(location);
            } catch (err) {
                console.error('Favorites.deleteFromFavorite', err);
            }
        }
    }

    saveFavoriteLocationsFromLocalStorage(
        currentFavoriteLocations: FavoriteLocation[]
    ): Observable<FavoriteLocation[]> {
        if (!this.authService.getLoginStatus()) {
            return of([]);
        }
        const locations = this.getFavoriteLocationsFromLocalStorage();

        const uniqueLocations = locations.filter(
            (location) =>
                !currentFavoriteLocations.some(
                    (currentLocation) =>
                        currentLocation.latitude === location.latitude &&
                        currentLocation.longitude === location.longitude
                )
        );

        localStorage.setItem(this.favoriteLocationsLocalStorageKey, JSON.stringify(uniqueLocations));

        if (!uniqueLocations) {
            return of([]);
        }

        return forkJoin(
            uniqueLocations.map((location: FavoriteLocation) =>
                this.addFavoriteLocation(location).pipe(
                    tap((location) => {
                        const locations = this.getFavoriteLocationsFromLocalStorage();

                        localStorage.setItem(
                            this.favoriteLocationsLocalStorageKey,
                            JSON.stringify(
                                locations.filter(
                                    (uLocation) =>
                                        location.latitude !== uLocation.latitude &&
                                        location.longitude !== uLocation.longitude
                                )
                            )
                        );
                    })
                )
            )
        );
    }

    // BE isn't ready. Replace to real requests
    getFavoriteTickets(): Observable<FavoriteTicket[]> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/ticket`;

        return this.http.get<FavoriteTicket[]>(url);
    }

    deleteFavoriteTickets(productId: number | string): Observable<any> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/ticket/${productId}`;

        return this.http.delete<FavoriteTicket>(url);
    }

    addFavoriteTicket(data: {
        docNum: string;
        categoryCode: string;
        productId: number;
        startDateStr?: string;
        endDateStr?: string;
        includeReturnedTickets?: boolean;
        includeCancelledTickets?: boolean;
        channels?: string[];
    }): Observable<FavoriteTicket> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/favorite/ticket`;

        return this.http.post<FavoriteTicket>(url, data);
    }

    getFavoritePreference(): Observable<FavoritePreference> {
        return of(JSON.parse(localStorage.getItem(this.favoritePreferenceLocalStorageKey)));
    }

    addFavoritePreference(payload): Observable<FavoritePreference> {
        localStorage.setItem(this.favoritePreferenceLocalStorageKey, JSON.stringify(payload));
        return of(payload);
    }

    deleteFavoritePreference(): Observable<any> {
        localStorage.removeItem(this.favoritePreferenceLocalStorageKey);
        return of([]);
    }

    getFavoriteScheduleFromLocalStorage(): FavoriteSchedule[] {
        return JsonUtil.parse<FavoriteSchedule[]>(
            localStorage.getItem(this.favoriteScheduleLocalStorageKey),
            Array.isArray,
            []
        );
    }

    getFavoriteSchedule(): Observable<FavoriteSchedule[]> {
        if (this.authService.getLoginStatus() && this.env.WMB?.WMB_PUBLIC_API) {
            const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/favorite/schedule`;

            return this.http.get<FavoriteSchedule[]>(url).pipe(map((value) => this.convertToCorrectFormat(value)));
        } else {
            return of(this.getFavoriteScheduleFromLocalStorage());
        }
    }

    addFavoriteSchedules(payload): Observable<FavoriteSchedule> {
        if (this.authService.getLoginStatus() && this.env.WMB?.WMB_PUBLIC_API) {
            const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/favorite/schedule`;

            return this.http
                .post<FavoriteSchedule>(url, payload)
                .pipe(map((value) => this.convertToCorrectFormat([value])[0]));
        } else {
            try {
                const prevValue = this.getFavoriteScheduleFromLocalStorage();

                localStorage.setItem(this.favoriteScheduleLocalStorageKey, JSON.stringify([...prevValue, payload]));

                return of(payload);
            } catch (err) {
                console.error('Favorites.addFavorites', err);
            }
        }
    }

    deleteFavoriteSchedule(deletedSchedule: FavoriteSchedule): Observable<FavoriteSchedule> {
        if (this.authService.getLoginStatus() && this.env.WMB?.WMB_PUBLIC_API) {
            const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/favorite/schedule/${deletedSchedule.id}`;

            return this.http.delete<FavoriteSchedule>(url);
        } else {
            try {
                const prevValue = this.getFavoriteScheduleFromLocalStorage();

                localStorage.setItem(
                    this.favoriteScheduleLocalStorageKey,
                    JSON.stringify(
                        prevValue.filter(
                            (s) =>
                                !(
                                    (s.stopId === deletedSchedule.stopId && s.routeId === deletedSchedule.routeId) ||
                                    (deletedSchedule?.id === s?.id && deletedSchedule?.id)
                                )
                        )
                    )
                );

                return of(deletedSchedule);
            } catch (err) {
                console.error('Favorites.deleteFromFavorite', err);
            }
        }
    }

    public convertToCorrectFormat(favoriteSchedules: FavoriteSchedule[]) {
        const routes = this.openTripPlannerService.getRoutes();
        const stops = this.openTripPlannerService.getStops();

        return favoriteSchedules.map((favoriteSchedule) => {
            const entity = {
                ...favoriteSchedule,
                route: favoriteSchedule.routeId ? routes.get(favoriteSchedule.routeId) : favoriteSchedule.route,
                stop: favoriteSchedule.stopId ? stops.get(favoriteSchedule.stopId) : favoriteSchedule.stop
            };
            return entity;
        });
    }

    saveFavoriteSchedulesFromLocalStorage(): Observable<FavoriteSchedule[]> {
        if (!this.authService.getLoginStatus()) {
            return of([]);
        }

        const schedules = this.getFavoriteScheduleFromLocalStorage();

        const uniqueSchedules = schedules.filter((schedule) => !schedule.id);

        localStorage.setItem(this.favoriteScheduleLocalStorageKey, JSON.stringify(uniqueSchedules));

        if (!uniqueSchedules) {
            return of([]);
        }

        return forkJoin(
            uniqueSchedules.map((schedule: FavoriteSchedule) =>
                this.addFavoriteSchedules(schedule).pipe(
                    tap((schedule) => {
                        const schedules = this.getFavoriteScheduleFromLocalStorage();

                        localStorage.setItem(
                            this.favoriteScheduleLocalStorageKey,
                            JSON.stringify(
                                schedules.filter(
                                    (lfs) =>
                                        !(schedule.routeId === lfs.routeId && schedule.stopId === lfs.stopId) ||
                                        (schedule.id === lfs.id && schedule.id)
                                )
                            )
                        );
                    })
                )
            )
        );
    }
}
